import { createSignal } from 'solid-js';
import { persistantStore } from './persistantStore';
import { selectedEvents, selectedRange, selectedAggregateEvents } from './selectionStore';
import { selectedFilters } from './filterStore';

export type AssistantPageDetails = Record<string, unknown>;

export type AssistantAskContext = {
  date?: string | null;
  dataset_id?: number | null;
  source_id?: number | null;
  source_name?: string | null;
  boat_names?: string[];
  event_name?: string | null;
  race_numbers?: number[];
  leg_numbers?: number[];
  selected_event_ids?: number[];
  selected_aggregate_event_ids?: number[];
  selected_range?: { start?: string | number; end?: string | number } | null;
  filters?: unknown[];
  page_details?: AssistantPageDetails;
  scope_kind?: string;
  menu_name?: string | null;
  page_name?: string | null;
  class_name?: string | null;
  project_id?: number | null;
};

const [pageDetails, setPageDetails] = createSignal<AssistantPageDetails>({});

/** Pages publish extra scope (plot settings, selected race, etc.). */
export function setAssistantPageDetails(details: AssistantPageDetails | null): void {
  setPageDetails(details && typeof details === 'object' ? { ...details } : {});
}

export function clearAssistantPageDetails(): void {
  setPageDetails({});
}

export function assistantPageDetails(): AssistantPageDetails {
  return pageDetails();
}

/**
 * Build the ask context payload: ids/names/bounds only — never channel series.
 */
export function buildAskContext(overrides?: Partial<AssistantAskContext>): AssistantAskContext {
  const date = persistantStore.selectedDate();
  const datasetId = persistantStore.selectedDatasetId();
  const sourceId = persistantStore.selectedSourceId();
  const sourceName = persistantStore.selectedSourceName();
  const menu = persistantStore.selectedMenu();
  const page = persistantStore.selectedPage();
  const projectId = persistantStore.selectedProjectId();
  const className = persistantStore.selectedClassName();

  const range = selectedRange();
  let selected_range: AssistantAskContext['selected_range'] = null;
  if (Array.isArray(range) && range.length >= 2) {
    selected_range = { start: range[0] as string | number, end: range[1] as string | number };
  }

  const details = pageDetails();
  const scopeKind =
    typeof details.scope_kind === 'string'
      ? details.scope_kind
      : sourceId === 0
        ? 'day'
        : datasetId
          ? 'dataset'
          : 'project';

  return {
    date: date || null,
    dataset_id: datasetId || null,
    source_id: sourceId || null,
    source_name: sourceName || null,
    boat_names: Array.isArray(details.boat_names) ? (details.boat_names as string[]) : undefined,
    event_name: typeof details.event_name === 'string' ? details.event_name : null,
    race_numbers: Array.isArray(details.race_numbers) ? (details.race_numbers as number[]) : undefined,
    leg_numbers: Array.isArray(details.leg_numbers) ? (details.leg_numbers as number[]) : undefined,
    selected_event_ids: selectedEvents(),
    selected_aggregate_event_ids: selectedAggregateEvents(),
    selected_range,
    filters: selectedFilters(),
    page_details: details,
    scope_kind: scopeKind,
    menu_name: menu || null,
    page_name: page || menu || null,
    class_name: className || null,
    project_id: projectId || null,
    ...overrides,
  };
}
